'use strict';

import _ from 'lodash';
import { EventEmitter } from 'events';
import AppDispatcher from '../dispatcher/AppDispatcher';
import HotelConstants from '../constants/HotelConstants';
import LazyRefreshConstants from '../constants/LazyRefreshConstants';
import { FIREBASE_STRUCTURE } from 'arbitrip-common/general/constants';
import FirebaseManager from 'arbitrip-common/client/utils/Firebase/Manager';
import RecheckAlternative from 'arbitrip-common/client/utils/RecheckAlternative';
import Analytics from 'arbitrip-common/client/analytics';
import ResultsStore from './ResultsStore';
import SearchStore from './SearchStore';
import HotelActions from '../actions/HotelActions';

const STATUS = {
    IDLE: 'idle',
    BUSY: 'busy',
    SUCCESS: 'success',
    NO_AVAILABILITY: 'no_availability',
    DEAL_CHANGED: 'deal_changed',
};

const { UNAVAILABLE_REASON } = RecheckAlternative;

let _status = STATUS.IDLE;
let _clicked_deal = null;
let _hotel_id = null;
let _lazy_deals = [];
let _lazy_deal_ids = [];
let _raw_recheck_deals = [];
let _deals_to_ban = [];
let _navigation_deal = null;
let _price_change_data = null;
let _unavailable_reason = null;
let _rejected_comparison = null;
let _listener_id = null;
let _is_expired = false;
let _changes_detected = false;
let _suggest_alternative = false;
let _alternatives_snapshot = null;
let _deal_changed_comparison = null;

const _refresh_contexts = new Map();

function _getCurrentRecheckToken() {
    return SearchStore.getHotelToken() || SearchStore.getToken();
}

function _removeRefreshContext(context) {
    if (context.listener_id) {
        FirebaseManager.recheck.unsubscribe(context.listener_id);
    }
    _refresh_contexts.delete(context.key);
}

function _abortIfStale(context) {
    if (context.token === _getCurrentRecheckToken()) {
        return false;
    }
    _removeRefreshContext(context);
    return true;
}

function _reset() {
    if (_listener_id) {
        FirebaseManager.recheck.unsubscribe(_listener_id);
    }
    _status = STATUS.IDLE;
    _clicked_deal = null;
    _hotel_id = null;
    _lazy_deals = [];
    _lazy_deal_ids = [];
    _raw_recheck_deals = [];
    _deals_to_ban = [];
    _navigation_deal = null;
    _price_change_data = null;
    _unavailable_reason = null;
    _rejected_comparison = null;
    _listener_id = null;
    _is_expired = false;
    _changes_detected = false;
    _suggest_alternative = false;
    _alternatives_snapshot = null;
    _deal_changed_comparison = null;
}

function _isActiveClickListener(listener_id) {
    return !!_listener_id && listener_id === _listener_id;
}

function _onFirebaseData(listener_id, rechecks) {
    if (!rechecks) return;

    const deals = _.get(rechecks, FIREBASE_STRUCTURE.RECHECK.FIELD, []);
    const messages = _.get(rechecks, FIREBASE_STRUCTURE.RECHECK.MESSAGE_TYPE, []);

    if (Array.isArray(deals) && deals.length > 0) {
        deals.forEach((raw_deal) => {
            AppDispatcher.handleAction({
                actionType: HotelConstants.LAZY_RECHECK_DEAL_RECEIVED,
                data: { listener_id, raw_deal },
            });
        });
    }

    const recheck_completed =
        Array.isArray(messages) &&
        messages.filter((msg) => msg === FIREBASE_STRUCTURE.RECHECK.MESSAGE_TYPE_RECHECK_COMPLETED);
    if (recheck_completed && recheck_completed.length > 0) {
        AppDispatcher.handleAction({
            actionType: HotelConstants.LAZY_RECHECK_COMPLETED,
            data: { listener_id },
        });
    }
}

function _onFirebaseError(listener_id, error) {
    console.error('LazyRecheckStore: Firebase error', error);
    AppDispatcher.handleAction({
        actionType: HotelConstants.LAZY_RECHECK_NO_AVAILABILITY,
        data: { listener_id },
    });
}

function _onFirebaseTimeout(listener_id) {
    console.log('_onFirebaseTimeout');
    AppDispatcher.handleAction({
        actionType: HotelConstants.LAZY_RECHECK_NO_AVAILABILITY,
        data: { listener_id },
    });
}

function _banDeals(hotel, deals) {
    if (!hotel || !deals.length) return;
    const snapshot = deals.slice();
    setTimeout(() => snapshot.forEach((deal) => HotelActions.banDeal(hotel, { ...deal, hide: true })), 0);
}

function _handleCompleted() {
    if (_listener_id) {
        FirebaseManager.recheck.unsubscribe(_listener_id);
        _listener_id = null;
    }
    if (_status !== STATUS.BUSY) {
        return;
    }

    const hotel = ResultsStore.getHotelById(_hotel_id);

    if (_raw_recheck_deals.length === 0) {
        _deals_to_ban = _lazy_deals.slice();
        _banDeals(hotel, _deals_to_ban);
        _unavailable_reason = UNAVAILABLE_REASON.NO_ALTERNATIVE;
        _status = STATUS.NO_AVAILABILITY;
        return;
    }

    if (hotel) {
        hotel.addRawRecheckedDeals(ResultsStore.getCurrentSearchSession(), _raw_recheck_deals);
    }

    const returned_ids = new Set(_raw_recheck_deals.map((d) => d.id));
    _deals_to_ban = _lazy_deals.filter((d) => !returned_ids.has(d.id));
    const deals_to_ban_ids = _deals_to_ban.map((d) => d.id);
    _banDeals(hotel, _deals_to_ban);

    const hotel_deals = (hotel && hotel.deals) || [];
    const available_deals = hotel_deals.filter(
        (d) =>
            returned_ids.has(d.id) &&
            d.recheck_id &&
            !deals_to_ban_ids.includes(d.id) &&
            !d.is_lazy_recheck,
    );
    if (available_deals.length === 0) {
        // Returned-but-faulty deals weren't caught by the not-returned filter; ban them too.
        const faulty_returned = _lazy_deals.filter((d) => returned_ids.has(d.id));
        _deals_to_ban = _deals_to_ban.concat(faulty_returned);
        _banDeals(hotel, faulty_returned);
        _unavailable_reason = UNAVAILABLE_REASON.NO_ALTERNATIVE;
        _status = STATUS.NO_AVAILABILITY;
        return;
    }

    if (!_changes_detected) {
        const returned_deal = available_deals.find((d) => d.id === _clicked_deal.id);
        if (!returned_deal) {
            _unavailable_reason = UNAVAILABLE_REASON.NO_ALTERNATIVE;
            _status = STATUS.NO_AVAILABILITY;
            return;
        }
        const comparison = RecheckAlternative.buildRecheckComparison(_clicked_deal, returned_deal);
        if (RecheckAlternative.hasAnyDealChange(comparison)) {
            _deal_changed_comparison = comparison;
            _unavailable_reason = UNAVAILABLE_REASON.DEAL_NO_LONGER_RELEVANT;
            _status = STATUS.DEAL_CHANGED;
            return;
        }
        _navigation_deal = returned_deal;
        returned_deal.superseded = false;
        _status = STATUS.SUCCESS;
        return;
    }

    const best_alternative = RecheckAlternative.pickBestAcceptableAlternative(_clicked_deal, available_deals);

    // Every alternative degraded the board basis or the cancellation policy without a price drop:
    // treat it as no availability. The rechecked deals stay in the list carrying their new terms,
    // so the original terms can never be booked again.
    if (!best_alternative) {
        _rejected_comparison = RecheckAlternative.buildRejectedComparison(_clicked_deal, available_deals);
        _unavailable_reason = _suggest_alternative
            ? UNAVAILABLE_REASON.ALTERNATIVE_REJECTED
            : UNAVAILABLE_REASON.TERMS_DOWNGRADED;
        _status = STATUS.NO_AVAILABILITY;
        return;
    }

    const { deal: best_deal, comparison } = best_alternative;

    _navigation_deal = best_deal;
    best_deal.superseded = false;
    _status = STATUS.SUCCESS;

    if (comparison.price_changed || comparison.deadline_changed || comparison.board_changed) {
        _price_change_data = {
            original_deal: _clicked_deal,
            best_deal,
            ...comparison,
        };
    }
}

function _onRefreshFirebaseData(refresh_key, listener_id, rechecks) {
    if (!rechecks) return;

    const deals = _.get(rechecks, FIREBASE_STRUCTURE.RECHECK.FIELD, []);
    const messages = _.get(rechecks, FIREBASE_STRUCTURE.RECHECK.MESSAGE_TYPE, []);

    if (Array.isArray(deals) && deals.length > 0) {
        deals.forEach((raw_deal) => {
            AppDispatcher.handleAction({
                actionType: LazyRefreshConstants.LAZY_REFRESH_DEAL_RECEIVED,
                data: { refresh_key, listener_id, raw_deal },
            });
        });
    }

    const recheck_completed =
        Array.isArray(messages) &&
        messages.some((msg) => msg === FIREBASE_STRUCTURE.RECHECK.MESSAGE_TYPE_RECHECK_COMPLETED);
    if (recheck_completed) {
        AppDispatcher.handleAction({
            actionType: LazyRefreshConstants.LAZY_REFRESH_COMPLETED,
            data: { refresh_key, listener_id },
        });
    }
}

function _onRefreshFirebaseTimeout(refresh_key, listener_id) {
    AppDispatcher.handleAction({
        actionType: LazyRefreshConstants.LAZY_REFRESH_TIMEOUT,
        data: { refresh_key, listener_id },
    });
}

function _subscribeRefresh(context, wait_time) {
    const { key, listener_id } = context;
    FirebaseManager.recheck.subscribe(
        listener_id,
        (rechecks) => _onRefreshFirebaseData(key, listener_id, rechecks),
        (error) => {
            console.error('LazyRecheckStore: refresh Firebase error', error);
            _onRefreshFirebaseTimeout(key, listener_id);
        },
        () => _onRefreshFirebaseTimeout(key, listener_id),
        wait_time,
    );
}

function _getActiveRefreshContext(refresh_key, listener_id) {
    const context = _refresh_contexts.get(refresh_key);
    if (!context || !context.listener_id || context.listener_id !== listener_id) {
        return null;
    }
    return context;
}

function _findHotelDeal(hotel, deal_id) {
    return ((hotel && hotel.deals) || []).find((d) => d.id === deal_id) || null;
}

function _handleRefreshDealReceived(context, raw_deal) {
    if (!raw_deal || !context.refreshing_ids.has(raw_deal.id)) {
        return false;
    }

    const hotel = ResultsStore.getHotelById(context.hotel_id);
    if (hotel) {
        hotel.addRawRecheckedDeals(ResultsStore.getCurrentSearchSession(), [raw_deal]);
    }
    context.refreshing_ids.delete(raw_deal.id);
    context.returned_ids.add(raw_deal.id);

    const before = context.before_deals[raw_deal.id];
    const after = _findHotelDeal(hotel, raw_deal.id);
    if (
        before &&
        after &&
        after !== before &&
        RecheckAlternative.hasAnyDealChange(RecheckAlternative.buildRecheckComparison(before, after))
    ) {
        context.changed_ids.add(raw_deal.id);
    }
    return true;
}

function _finishRefresh(context, completed) {
    const hotel = ResultsStore.getHotelById(context.hotel_id);

    const not_returned_deals = completed
        ? Array.from(context.refreshing_ids)
              .map((id) => _findHotelDeal(hotel, id) || context.before_deals[id])
              .filter(Boolean)
        : [];
    const faulty_returned_deals = Array.from(context.returned_ids)
        .map((id) => _findHotelDeal(hotel, id))
        .filter((deal) => deal && (deal.is_lazy_recheck || !deal.recheck_id));
    const removed_deals = not_returned_deals.concat(faulty_returned_deals);
    _banDeals(hotel, removed_deals);

    const removed_ids = new Set(removed_deals.map((d) => d.id));
    const deals_changed = Array.from(context.changed_ids).filter((id) => !removed_ids.has(id)).length;

    const { analytics } = context;
    Analytics.actions.responses.roomRateCheckListRefreshed({
        user: analytics.user,
        search_id: analytics.search_id,
        hotel: analytics.hotel,
        trigger_popup: analytics.trigger_popup,
        deals_rechecked: context.deal_ids.length,
        deals_changed,
        deals_removed: removed_deals.length,
        duration_ms: Date.now() - context.started_at,
        changes_detected_flag: analytics.changes_detected,
        suggest_alternative_flag: analytics.suggest_alternative,
    });

    _removeRefreshContext(context);
}

const LazyRecheckStore = _.extend({}, EventEmitter.prototype, {
    emitChange: function () {
        this.emit('change');
    },
    addChangeListener: function (callback) {
        this.on('change', callback);
    },
    removeChangeListener: function (callback) {
        this.removeListener('change', callback);
    },

    getStatus: function () {
        return _status;
    },
    isBusy: function () {
        return _status === STATUS.BUSY;
    },
    isLoadingDeal: function (deal_id) {
        return _status === STATUS.BUSY && _lazy_deal_ids.includes(deal_id);
    },
    getClickedDeal: function () {
        return _clicked_deal;
    },
    getNavigationDeal: function () {
        return _navigation_deal;
    },
    getPriceChangeData: function () {
        return _price_change_data;
    },
    getDealsToBan: function () {
        return _deals_to_ban;
    },
    isNoAvailability: function () {
        return _status === STATUS.NO_AVAILABILITY;
    },
    getUnavailableReason: function () {
        return _unavailable_reason;
    },
    getRejectedComparison: function () {
        return _rejected_comparison;
    },
    getAlternativesSnapshot: function () {
        return _alternatives_snapshot;
    },
    isSuggestAlternativeEnabled: function () {
        return _suggest_alternative;
    },
    isChangesDetectedEnabled: function () {
        return _changes_detected;
    },
    isDealChanged: function () {
        return _status === STATUS.DEAL_CHANGED;
    },
    getDealChangedComparison: function () {
        return _deal_changed_comparison;
    },
    getRecheckToken: function () {
        return _getCurrentRecheckToken();
    },
    isRefreshingDeal: function (hotel_id, deal_id) {
        const token = _getCurrentRecheckToken();
        return Array.from(_refresh_contexts.values()).some(
            (context) =>
                context.hotel_id === hotel_id && context.token === token && context.refreshing_ids.has(deal_id),
        );
    },
    isExpired: function () {
        return _is_expired;
    },
    STATUS,
});

AppDispatcher.register(function (payload) {
    const action = payload.action;

    switch (action.actionType) {
        case HotelConstants.LAZY_RECHECK_START: {
            _reset();
            _clicked_deal = action.data.clicked_deal;
            _hotel_id = action.data.hotel_id;
            _lazy_deals = action.data.lazy_deals;
            _changes_detected = action.data.changes_detected === true;
            _suggest_alternative = action.data.suggest_alternative === true;
            _alternatives_snapshot = action.data.alternatives_snapshot || null;
            _lazy_deal_ids = _lazy_deals.map((d) => d.id);
            _status = STATUS.BUSY;
            break;
        }

        case HotelConstants.LAZY_RECHECK_REQUESTED: {
            const { listener_id, wait_time } = action.data;
            _listener_id = listener_id;
            FirebaseManager.recheck.subscribe(
                listener_id,
                (rechecks) => _onFirebaseData(listener_id, rechecks),
                (error) => _onFirebaseError(listener_id, error),
                () => _onFirebaseTimeout(listener_id),
                wait_time,
            );
            break;
        }

        case HotelConstants.LAZY_RECHECK_DEAL_RECEIVED: {
            const { listener_id, raw_deal } = action.data;
            if (!_isActiveClickListener(listener_id)) {
                return true;
            }
            if (raw_deal && !_raw_recheck_deals.find((d) => d.id === raw_deal.id)) {
                _raw_recheck_deals.push(raw_deal);
            }
            if (raw_deal?.id === _clicked_deal.id) {
                _handleCompleted();
                break;
            }
            return true;
        }

        case HotelConstants.LAZY_RECHECK_COMPLETED: {
            if (!_isActiveClickListener(action.data.listener_id)) {
                return true;
            }
            _handleCompleted();
            break;
        }

        case HotelConstants.LAZY_RECHECK_EXPIRED: {
            _is_expired = true;
            _status = STATUS.IDLE;
            break;
        }

        case HotelConstants.LAZY_RECHECK_NO_AVAILABILITY: {
            // Firebase-originated (timeout/error) carries its listener id; an earlier click's
            // expiry timer must not mark the current click's deals unavailable.
            const { listener_id } = action.data || {};
            if (listener_id && !_isActiveClickListener(listener_id)) {
                return true;
            }
            if (_listener_id) {
                FirebaseManager.recheck.unsubscribe(_listener_id);
                _listener_id = null;
            }

            // The Firebase subscription timeout still fires after a recheck completed
            // successfully. Banning here would ban the deals of a finished recheck — including
            // the one the user was sent to — restoring their pre-recheck terms and hiding them.
            if (_status !== STATUS.BUSY) {
                break;
            }

            _unavailable_reason = UNAVAILABLE_REASON.NO_ALTERNATIVE;
            _status = STATUS.NO_AVAILABILITY;

            if (_lazy_deals.length > 0) {
                _banDeals(ResultsStore.getHotelById(_hotel_id), _lazy_deals.slice());
            }
            break;
        }

        case HotelConstants.LAZY_RECHECK_CONFIRM_PRICE_CHANGE: {
            _price_change_data = null;
            break;
        }

        case HotelConstants.LAZY_RECHECK_RESET: {
            _reset();
            break;
        }

        case LazyRefreshConstants.LAZY_REFRESH_START: {
            const {
                refresh_key,
                token,
                hotel_id,
                deal_ids,
                trigger_popup,
                search_id,
                hotel,
                user,
                changes_detected,
                suggest_alternative,
            } = action.data;
            Array.from(_refresh_contexts.values()).forEach((context) => {
                if (context.key === refresh_key) {
                    _removeRefreshContext(context);
                } else {
                    _abortIfStale(context);
                }
            });
            const hotel_entity = ResultsStore.getHotelById(hotel_id);
            const before_deals = {};
            deal_ids.forEach((id) => {
                before_deals[id] = _findHotelDeal(hotel_entity, id);
            });
            _refresh_contexts.set(refresh_key, {
                key: refresh_key,
                token,
                hotel_id,
                listener_id: null,
                deal_ids: deal_ids.slice(),
                refreshing_ids: new Set(deal_ids),
                returned_ids: new Set(),
                changed_ids: new Set(),
                before_deals,
                started_at: Date.now(),
                analytics: { trigger_popup, search_id, hotel, user, changes_detected, suggest_alternative },
            });
            break;
        }

        case LazyRefreshConstants.LAZY_REFRESH_REQUESTED: {
            const { refresh_key, listener_id, wait_time } = action.data;
            const context = _refresh_contexts.get(refresh_key);
            if (!context || context.listener_id) {
                return true;
            }
            if (_abortIfStale(context)) {
                break;
            }
            if (!listener_id) {
                _removeRefreshContext(context);
                break;
            }
            context.listener_id = listener_id;
            _subscribeRefresh(context, wait_time);
            return true;
        }

        case LazyRefreshConstants.LAZY_REFRESH_DEAL_RECEIVED: {
            const { refresh_key, listener_id, raw_deal } = action.data;
            const context = _getActiveRefreshContext(refresh_key, listener_id);
            if (!context) {
                return true;
            }
            if (_abortIfStale(context)) {
                break;
            }
            if (!_handleRefreshDealReceived(context, raw_deal)) {
                return true;
            }
            if (context.refreshing_ids.size === 0) {
                _finishRefresh(context, true);
            }
            break;
        }

        case LazyRefreshConstants.LAZY_REFRESH_COMPLETED:
        case LazyRefreshConstants.LAZY_REFRESH_TIMEOUT: {
            const { refresh_key, listener_id } = action.data;
            const context = _getActiveRefreshContext(refresh_key, listener_id);
            if (!context) {
                return true;
            }
            if (_abortIfStale(context)) {
                break;
            }
            _finishRefresh(context, action.actionType === LazyRefreshConstants.LAZY_REFRESH_COMPLETED);
            break;
        }

        case LazyRefreshConstants.LAZY_REFRESH_FAILED: {
            const context = _refresh_contexts.get(action.data.refresh_key);
            if (!context) {
                return true;
            }
            _removeRefreshContext(context);
            break;
        }

        default:
            return true;
    }

    LazyRecheckStore.emitChange();
    return true;
});

export default LazyRecheckStore;
